/**
 * @fileoverview App-wide co-signing (FROST) ceremony overlay.
 *
 * When a co-signed payment is in flight anywhere in the app (e.g. paying for a
 * mint with a loaded `.wult` share), {@link ZcashWalletService} drives a single
 * ceremony state. This overlay subscribes to it and shows the WB32COSIGN
 * pairing QR plus live status, so the user can complete the ceremony on their
 * second device without the triggering surface needing to know anything about
 * co-signing. Presentation only — all logic lives in the service.
 */

import React, { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Smartphone, Loader2, Check, AlertTriangle, ExternalLink, X } from 'lucide-react';
import { zcashWallet, type CosignCeremonyState } from '../../services/zcashWallet';

const EXPLORER_TX = 'https://mainnet.zcashexplorer.app/transactions/';

const truncate = (s: string, head = 16, tail = 10): string =>
	s.length <= head + tail ? s : `${s.slice(0, head)}…${s.slice(-tail)}`;

const CosignCeremonyOverlay: React.FC = () => {
	const [state, setState] = useState<CosignCeremonyState>(() => zcashWallet.getCosignCeremony());

	useEffect(() => {
		// Sync once on mount (state may have changed before we subscribed) then
		// re-read on every change — the service is the single source of truth.
		setState(zcashWallet.getCosignCeremony());
		return zcashWallet.onCosignCeremonyChange(() => setState(zcashWallet.getCosignCeremony()));
	}, []);

	if (!state.active) return null;

	const inProgress = state.phase !== 'done' && state.phase !== 'error';

	return (
		<div className="fixed inset-0 z-[90] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Co-signing ceremony">
			<div className="fixed inset-0 bg-black/60" />
			<div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden">
				<div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-4 flex items-center justify-between">
					<h2 className="text-lg font-bold text-white flex items-center gap-2">
						<Smartphone className="w-5 h-5" /> Co-sign to pay
					</h2>
					{!inProgress && (
						<button
							type="button"
							onClick={() => zcashWallet.dismissCosignCeremony()}
							className="text-white hover:text-gray-200"
							aria-label="Close"
						>
							<X className="w-6 h-6" />
						</button>
					)}
				</div>

				<div className="p-6 text-center">
					{state.phase === 'done' ? (
						<>
							<Check className="w-10 h-10 text-emerald-600 mx-auto" />
							<p className="text-sm font-medium text-emerald-900 mt-2">Payment broadcast</p>
							{state.txid && (
								<a
									href={`${EXPLORER_TX}${state.txid}`}
									target="_blank"
									rel="noopener noreferrer"
									className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:text-emerald-900 mt-1 break-all"
								>
									{truncate(state.txid)} <ExternalLink className="w-3 h-3" />
								</a>
							)}
							<button
								type="button"
								onClick={() => zcashWallet.dismissCosignCeremony()}
								className="mt-4 w-full px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm"
							>
								Done
							</button>
						</>
					) : state.phase === 'error' ? (
						<>
							<div className="flex items-start gap-2 text-left">
								<AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
								<div className="flex-1">
									<p className="text-sm font-medium text-red-900">Co-signing failed</p>
									<p className="text-xs text-red-700 mt-1 break-words">{state.error}</p>
								</div>
							</div>
							<button
								type="button"
								onClick={() => zcashWallet.dismissCosignCeremony()}
								className="mt-4 w-full px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 text-sm"
							>
								Close
							</button>
						</>
					) : state.qrPayload ? (
						<>
							<p className="text-sm font-medium text-emerald-900 flex items-center justify-center gap-2">
								<Smartphone className="w-4 h-4" /> Pair your cosigner
							</p>
							<div className="bg-white inline-block p-3 rounded-lg mt-3 shadow-sm ring-1 ring-gray-100">
								<QRCodeSVG value={state.qrPayload} size={200} level="L" />
							</div>
							<p className="text-xs text-emerald-700 mt-2">
								Open the Winbit32 cosigner on your other device and scan this code to approve the payment.
							</p>
							<p className="text-sm text-emerald-800 mt-3 flex items-center justify-center gap-2">
								<Loader2 className="w-4 h-4 animate-spin" /> {state.message || 'Waiting for your cosigner…'}
							</p>
							<button
								type="button"
								onClick={() => zcashWallet.cancelCosignCeremony()}
								className="mt-4 w-full px-4 py-2 border border-emerald-300 text-emerald-700 rounded-lg hover:bg-emerald-50 text-sm"
							>
								Cancel
							</button>
						</>
					) : (
						<>
							<Loader2 className="w-10 h-10 text-emerald-600 mx-auto animate-spin" />
							<p className="text-sm text-emerald-800 mt-3">{state.message || 'Working…'}</p>
							<button
								type="button"
								onClick={() => zcashWallet.cancelCosignCeremony()}
								className="mt-4 w-full px-4 py-2 border border-emerald-300 text-emerald-700 rounded-lg hover:bg-emerald-50 text-sm"
							>
								Cancel
							</button>
						</>
					)}
				</div>
			</div>
		</div>
	);
};

export default CosignCeremonyOverlay;
